// ============================================
// PAGE - Register
// ============================================

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSignupInitiate } from '@almacenes/core';
import { Button, Input } from '@almacenes/ui';
import logo from '@/assets/logos/logo.svg';
import pcstoreImg from '@/assets/images/pcstore.svg';
import { useRutFormatter } from '@/hooks/useRutFormatter';
import { useOnboardingOptions } from '@/hooks/useOnboardingOptions';

export const RegisterPage = () => {
  const navigate = useNavigate();
  const { mutate: signupInitiate, isPending } = useSignupInitiate();
  const { data: onboardingOptions, isLoading: isLoadingOptions } = useOnboardingOptions();

  // Formateo de RUT del negocio
  const { 
    rawValue: rutNegocio, 
    formattedValue: formattedRutNegocio, 
    handleChange: handleRutNegocioChange, 
    isValid: isRutNegocioValid 
  } = useRutFormatter();

  // Formateo de RUN personal
  const { 
    rawValue: run, 
    formattedValue: formattedRun, 
    handleChange: handleRunChange, 
    isValid: isRunValid 
  } = useRutFormatter();

  const {
    formattedValue: formattedRutVendedor,
    handleChange: handleRutVendedorChange,
    isValid: isRutVendedorValid,
    reset: resetRutVendedor,
  } = useRutFormatter();

  const [formData, setFormData] = useState({
    nombre: '',
    apellidos: '',
    email: '',
    emailConfirm: '',
    telefono: '',
    comoTeEnteraste: '',
    comoTeEnterasteOtro: '',
  });

  const [errors, setErrors] = useState({
    rutNegocio: '',
    run: '',
    rutVendedor: '',
    email: '',
    telefono: '',
  });

  const [apiError, setApiError] = useState<string | null>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Limpiar mensajes previos
    setApiError(null);
    
    let hasErrors = false;
    const newErrors = { rutNegocio: '', run: '', rutVendedor: '', email: '', telefono: '' };

    // Validar RUT del negocio
    if (!isRutNegocioValid()) {
      newErrors.rutNegocio = 'El RUT del negocio no es válido';
      hasErrors = true;
    }

    // Validar RUN personal
    if (!isRunValid()) {
      newErrors.run = 'El RUN personal no es válido';
      hasErrors = true;
    }

    if (formData.comoTeEnteraste === 'Vendedor' && !isRutVendedorValid()) {
      newErrors.rutVendedor = 'El RUT del vendedor no es válido';
      hasErrors = true;
    }

    // Validar que los correos coincidan
    if (formData.email !== formData.emailConfirm) {
      newErrors.email = 'Los correos electrónicos no coinciden';
      hasErrors = true;
    }

    // Validar teléfono (debe tener exactamente 9 dígitos)
    if (formData.telefono.length !== 9) {
      newErrors.telefono = 'El número de teléfono debe tener 9 dígitos';
      hasErrors = true;
    }

    setErrors(newErrors);

    if (hasErrors) {
      return;
    }

    // Enviar datos al backend
    signupInitiate(
      {
        rutAlmacen: rutNegocio,
        runPersonal: run,
        nombrePropietario: formData.nombre,
        apellidosPropietario: formData.apellidos,
        email: formData.email,
        telefono: parseInt(formData.telefono, 10),
        medioInformacion: formData.comoTeEnteraste === 'Otro'
          ? formData.comoTeEnterasteOtro
          : formData.comoTeEnteraste === 'Vendedor'
            ? `Vendedor: ${formattedRutVendedor}`
            : formData.comoTeEnteraste,
      },
      {
        onSuccess: () => {
          // Navegar a la página de éxito
          navigate('/auth/register-success');
        },
        onError: (error: any) => {
          // Manejar error 409 - Ya existe almacén con ese RUT
          if (error?.code === 'BUSINESS_RULE_VIOLATION') {
            setApiError(error.message);
          } else {
            // Otros errores
            setApiError(error?.message || 'Error al registrar. Por favor, intenta nuevamente.');
          }
        },
      }
    );
  };

  return (
    <div className="min-h-screen h-screen flex flex-col overflow-hidden">
      {/* Logo en la esquina superior izquierda */}
      <div className="bg-white border-b border-gray-200 px-8 py-4 flex items-center">
        <img 
          src={logo} 
          alt="Almacenes Digitales" 
          className="h-9"
        />
      </div>

      {/* Layout principal - 2 columnas */}
      <div className="flex-1 grid lg:grid-cols-2 h-full">
        {/* Columna izquierda - Formulario */}
        <div className="flex items-start justify-center px-8 py-8 h-full overflow-auto" style={{ backgroundColor: '#F3F4F9' }}>
          <div className="w-full max-w-4xl">
            {/* Card del formulario */}
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <h2 className="text-title-3 mb-6">Datos de registro</h2>

              {/* Mensaje de error */}
              {apiError && (
                <div className="mb-4 rounded-lg bg-red-50 p-4 text-sm text-red-800 border border-red-200">
                  <div className="flex items-start">
                    <svg className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    <p>{apiError}</p>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Fila 1: RUT del negocio y RUN */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div>
                    <Input
                      label="Ingresa el RUT de tu negocio"
                      type="text"
                      required
                      value={formattedRutNegocio}
                      onChange={(e) => {
                        handleRutNegocioChange(e);
                        setErrors({ ...errors, rutNegocio: '' });
                      }}
                      className={`text-base ${errors.rutNegocio ? 'border-red-500' : ''}`}
                    />
                    {errors.rutNegocio && (
                      <p className="mt-1 text-sm text-red-600">{errors.rutNegocio}</p>
                    )}
                  </div>

                  <div>
                    <Input
                      label="Ingresa tu RUN"
                      type="text"
                      required
                      value={formattedRun}
                      onChange={(e) => {
                        handleRunChange(e);
                        setErrors({ ...errors, run: '' });
                      }}
                      className={`text-base ${errors.run ? 'border-red-500' : ''}`}
                    />
                    {errors.run && (
                      <p className="mt-1 text-sm text-red-600">{errors.run}</p>
                    )}
                  </div>
                </div>

                {/* Fila 2: Nombre y Apellidos */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <Input
                    label="Ingresa tu nombre"
                    type="text"
                    required
                    value={formData.nombre}
                    onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                    className="text-base"
                  />

                  <Input
                    label="Ingresa tus apellidos"
                    type="text"
                    required
                    value={formData.apellidos}
                    onChange={(e) => setFormData({ ...formData, apellidos: e.target.value })}
                    className="text-base"
                  />
                </div>

                {/* Fila 3: Correo y Repetir Correo */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <Input
                    label="Ingresa tu correo electrónico"
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => {
                      setFormData({ ...formData, email: e.target.value });
                      setErrors({ ...errors, email: '' });
                    }}
                    className={`text-base ${errors.email ? 'border-red-500' : ''}`}
                  />

                  <div>
                    <Input
                      label="Repite tu correo electrónico"
                      type="email"
                      required
                      value={formData.emailConfirm}
                      onChange={(e) => {
                        setFormData({ ...formData, emailConfirm: e.target.value });
                        setErrors({ ...errors, email: '' });
                      }}
                      className={`text-base ${errors.email ? 'border-red-500' : ''}`}
                    />
                    {errors.email && (
                      <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                    )}
                  </div>
                </div>

                {/* Fila 4: Teléfono */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ingresa tu número de teléfono
                  </label>
                  <div className={`flex items-center border rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-primary focus-within:border-transparent ${errors.telefono ? 'border-red-500' : 'border-gray-300'}`}>
                    <span className="px-4 py-3 bg-gray-50 text-gray-600 text-base font-medium border-r border-gray-300">
                      +56
                    </span>
                    <input
                      type="tel"
                      required
                      value={formData.telefono}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '').slice(0, 9);
                        setFormData({ ...formData, telefono: value });
                        setErrors({ ...errors, telefono: '' });
                      }}
                      placeholder="912345678"
                      className="flex-1 px-4 py-3 text-base outline-none"
                      maxLength={9}
                      pattern="[0-9]{9}"
                      title="El número debe tener exactamente 9 dígitos"
                    />
                  </div>
                  {errors.telefono && (
                    <p className="mt-1 text-sm text-red-600">{errors.telefono}</p>
                  )}
                </div>

                {/* Fila 5: Cómo te enteraste */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 items-start">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      ¿Cómo te enteraste de nosotros?
                    </label>
                    <select
                      required
                      value={formData.comoTeEnteraste}
                      onChange={(e) => {
                        const value = e.target.value;
                        setFormData({
                          ...formData,
                          comoTeEnteraste: value,
                          comoTeEnterasteOtro: value !== 'Otro' ? '' : formData.comoTeEnterasteOtro,
                        });
                        if (value !== 'Vendedor') {
                          resetRutVendedor();
                          setErrors({ ...errors, rutVendedor: '' });
                        }
                      }}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-base"
                      disabled={isLoadingOptions}
                    >
                      <option value="">Selecciona una opción</option>
                      {[...(onboardingOptions?.leadSources ?? []),
                        ...((onboardingOptions?.leadSources ?? []).some((option) => option.value === 'Vendedor')
                          ? []
                          : [{ value: 'Vendedor', label: 'Vendedor' }])].map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {formData.comoTeEnteraste === 'Otro' && (
                    <input
                      type="text"
                      required
                      value={formData.comoTeEnterasteOtro}
                      onChange={(e) => setFormData({ ...formData, comoTeEnterasteOtro: e.target.value })}
                      placeholder="Especifica"
                      aria-label="Especifica cómo te enteraste"
                      className="w-full self-end px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-base"
                    />
                  )}

                  {formData.comoTeEnteraste === 'Vendedor' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Ingresa el RUT del vendedor
                      </label>
                      <input
                        type="text"
                        required
                        value={formattedRutVendedor}
                        onChange={(e) => {
                          handleRutVendedorChange(e);
                          setErrors({ ...errors, rutVendedor: '' });
                        }}
                        placeholder="Ej: 12.345.678-9"
                        className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-base ${errors.rutVendedor ? 'border-red-500' : 'border-gray-300'}`}
                      />
                      {errors.rutVendedor && (
                        <p className="mt-1 text-sm text-red-600">{errors.rutVendedor}</p>
                      )}
                    </div>
                  )}
                </div>

                <Button
                  type="submit"
                  variant="primary"
                  className="w-full py-4"
                  size="lg"
                  isLoading={isPending}
                >
                  Registrarme
                </Button>

                <div className="text-center pt-4">
                  <span className="text-body">¿Ya tienes cuenta? </span>
                  <Link to="/auth/login" className="text-link font-medium">
                    Inicia sesión
                  </Link>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* Columna derecha - Ilustración */}
        <div 
          className="hidden lg:flex items-center justify-center p-12 h-full"
          style={{ 
            background: 'linear-gradient(135deg, #FFE27A 0%, #F9D949 100%)'
          }}
        >
          <div className="max-w-2xl">
            <img 
              src={pcstoreImg} 
              alt="E-commerce Illustration" 
              className="w-full h-auto"
            />
          </div>
        </div>
      </div>
    </div>
  );
};
